import React, { useCallback, useState } from 'react';
import { Archive, Loader2, Pencil, Plus, Tag } from 'lucide-react';
import type {
  CallAnalysisTagDefinitionChoice,
  CallAnalysisTagDefinitionResponse,
} from '@reach/shared-types/call-analysis-tag-definitions';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { ConfirmDialog } from './ConfirmDialog';
import { choicePillInlineStyles } from './tag-choice-pill-utils';
import { resolveTagIcon } from './tag-icon-utils';
import { TagDefinitionDialog } from './TagDefinitionDialog';
import {
  useArchiveTagDefinition,
  useCallAnalysisTagDefinitions,
  useCreateTagDefinition,
  useUpdateTagDefinition,
} from './useCallAnalysisTagDefinitions';
import styles from './CallAnalysisTagsTab.module.css';

export const CallAnalysisTagsTab: React.FC = () => {
  const { data: definitions, isLoading, error } = useCallAnalysisTagDefinitions();
  const createMutation = useCreateTagDefinition();
  const updateMutation = useUpdateTagDefinition();
  const archiveMutation = useArchiveTagDefinition();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingDef, setEditingDef] = useState<CallAnalysisTagDefinitionResponse | null>(null);
  const [archivingDef, setArchivingDef] = useState<CallAnalysisTagDefinitionResponse | null>(null);

  const handleCreate = useCallback(
    (data: {
      name: string;
      type: 'boolean' | 'enum';
      description: string;
      choices?: CallAnalysisTagDefinitionChoice[];
    }) => {
      createMutation.mutate(data, {
        onSuccess: () => setIsCreateOpen(false),
      });
    },
    [createMutation],
  );

  const handleUpdate = useCallback(
    (data: {
      name: string;
      type: 'boolean' | 'enum';
      description: string;
      choices?: CallAnalysisTagDefinitionChoice[];
    }) => {
      if (!editingDef) return;
      updateMutation.mutate(
        {
          id: editingDef.id,
          body: {
            description: data.description,
            ...(data.choices !== undefined ? { choices: data.choices } : {}),
          },
        },
        { onSuccess: () => setEditingDef(null) },
      );
    },
    [editingDef, updateMutation],
  );

  const handleArchive = useCallback(() => {
    if (!archivingDef) return;
    archiveMutation.mutate(archivingDef.id, {
      onSuccess: () => setArchivingDef(null),
    });
  }, [archivingDef, archiveMutation]);

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Loader2 className={styles.loadingIcon} />
      </div>
    );
  }

  if (error) {
    return (
      <Card className={styles.errorCard}>
        <p className={styles.errorText}>Failed to load tag definitions: {error.message}</p>
      </Card>
    );
  }

  const tagDefs = definitions ?? [];

  return (
    <div className={styles.root}>
      <div className={styles.headerRow}>
        <div>
          <h3 className={styles.headerTitle}>Call Analysis Tags</h3>
          <p className={styles.headerDescription}>
            Define tags that the AI evaluates on every call. Tags are applied automatically during
            post-call analysis.
          </p>
        </div>
        <Button size="sm" onClick={() => setIsCreateOpen(true)}>
          <Plus />
          Add Tag
        </Button>
      </div>

      {tagDefs.length === 0 ? (
        <Card className={styles.emptyCard}>
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <Tag />
            </div>
            <h4 className={styles.emptyTitle}>No tag definitions yet</h4>
            <p className={styles.emptyDescription}>
              Create your first tag definition to start categorizing calls with AI. Post-call analysis
              only runs after you define at least one tag.
            </p>
            <Button size="sm" className={styles.emptyButton} onClick={() => setIsCreateOpen(true)}>
              <Plus />
              Create First Tag
            </Button>
          </div>
        </Card>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.theadRow}>
                <th className={styles.th}>Name</th>
                <th className={styles.th}>Type</th>
                <th className={styles.th}>Description</th>
                <th className={styles.th}>Choices</th>
                <th className={`${styles.th} ${styles.thRight}`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tagDefs.map(def => (
                <tr key={def.id} className={styles.tbodyRow}>
                  <td className={styles.nameCell}>
                    <code className={styles.nameCode}>{def.name}</code>
                  </td>
                  <td className={styles.typeCell}>
                    <span className={styles.typePill} data-type={def.type}>
                      {def.type}
                    </span>
                  </td>
                  <td className={styles.descriptionCell}>{def.description}</td>
                  <td className={styles.choicesCell}>
                    {def.choices ? (
                      <div className={styles.choicesList}>
                        {def.choices.slice(0, 4).map((c, idx) => {
                          const ResolvedIcon = c.icon ? resolveTagIcon(c.icon) : null;
                          const pillStyle = choicePillInlineStyles(c.color, c.textColor);
                          const pillTitle = c.tooltipDescription ?? c.displayName ?? c.value;
                          return (
                            <span
                              key={`${c.value}-${idx}`}
                              className={styles.choicePill}
                              style={pillStyle}
                              title={pillTitle}>
                              {ResolvedIcon ? (
                                <span className={styles.choicePillIcon} title={c.icon}>
                                  <ResolvedIcon aria-hidden size={12} />
                                </span>
                              ) : null}
                              <span className={styles.choicePillText}>{c.displayName ?? c.value}</span>
                            </span>
                          );
                        })}
                        {def.choices.length > 4 && (
                          <span className={styles.choicesMore}>+{def.choices.length - 4} more</span>
                        )}
                      </div>
                    ) : (
                      <span className={styles.choicesEmpty}>—</span>
                    )}
                  </td>
                  <td className={styles.actionsCell}>
                    <div className={styles.actions}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditingDef(def)}
                        title="Edit">
                        <Pencil />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setArchivingDef(def)}
                        title="Archive">
                        <Archive />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TagDefinitionDialog
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={handleCreate}
        isPending={createMutation.isPending}
      />

      <TagDefinitionDialog
        open={!!editingDef}
        onClose={() => setEditingDef(null)}
        onSubmit={handleUpdate}
        isPending={updateMutation.isPending}
        existing={editingDef}
      />

      <ConfirmDialog
        open={!!archivingDef}
        onClose={() => setArchivingDef(null)}
        onConfirm={handleArchive}
        isPending={archiveMutation.isPending}
        title="Archive tag definition"
        description={`Are you sure you want to archive "${archivingDef?.name}"? It will no longer be evaluated on new calls. Existing tags on past calls will be preserved.`}
        confirmLabel="Archive"
      />
    </div>
  );
};
